import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  ChartBarIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  PlusIcon,
  ShieldCheckIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';
import importExportService from '../../services/importExportService';
import tenantService from '../../services/tenantService';
import { useToast } from '../../contexts/ToastContext';

const institutionTemplateOptions = [
  { value: 'microfinance', label: 'Micro-finance' },
  { value: 'microcredit', label: 'Micro-crédit' },
  { value: 'mutual', label: 'Mutuelle' },
  { value: 'microbank', label: 'Micro-banque' },
  { value: 'cooperative', label: 'Coopérative financière' },
];

const formatDate = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
};

const GlobalDashboard = () => {
  const [tenants, setTenants] = useState([]);
  const [institutionTypes, setInstitutionTypes] = useState([]);
  const [selectedTemplateType, setSelectedTemplateType] = useState('microfinance');
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [loading, setLoading] = useState(true);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const [tenantData, typeData] = await Promise.all([
          tenantService.getTenants(),
          tenantService.getInstitutionTypes().catch(() => []),
        ]);
        setTenants(tenantData || []);
        setInstitutionTypes(typeData || []);
      } catch (error) {
        showError('Erreur lors du chargement du dashboard global');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [showError]);

  const stats = useMemo(() => {
    const byType = tenants.reduce((acc, tenant) => {
      const type = tenant.institution_type || 'microfinance';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    const currencies = Array.from(new Set(
      tenants
        .map((tenant) => tenant.settings?.general?.currency || tenant.currency)
        .filter(Boolean)
    ));

    return {
      totalTenants: tenants.length,
      activeTypes: Object.keys(byType).length,
      currencies,
      byType,
    };
  }, [tenants]);

  const typeLabel = (typeValue) => (
    institutionTypes.find((type) => type.value === typeValue)?.label || typeValue
  );

  const recentTenants = useMemo(() => (
    [...tenants]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 5)
  ), [tenants]);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const blob = await importExportService.downloadTemplate(selectedTemplateType);
      downloadBlob(blob, `modele_import_${selectedTemplateType}.xlsx`);
      showSuccess('Modèle Excel téléchargé');
    } catch (error) {
      showError('Erreur lors du téléchargement du modèle');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">PRONTO Core</h1>
          <p className="mt-1 text-sm text-gray-600">Tableau de bord global pour créer et configurer les institutions financières.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Link to="/global/settings" className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <BuildingOffice2Icon className="h-5 w-5" />
            Liste des tenants
          </Link>
          <Link to="/global/settings/new" className="btn-primary inline-flex items-center justify-center gap-2">
            <PlusIcon className="h-5 w-5" />
            Nouveau tenant
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white px-5 py-10 text-sm text-gray-500 shadow-sm">Chargement...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Institutions</p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">{stats.totalTenants}</p>
                </div>
                <BuildingOffice2Icon className="h-10 w-10 text-primary-600" />
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Types actifs</p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">{stats.activeTypes}</p>
                </div>
                <ChartBarIcon className="h-10 w-10 text-emerald-600" />
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Devises</p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">{stats.currencies.length || 0}</p>
                </div>
                <WalletIcon className="h-10 w-10 text-amber-600" />
              </div>
              <p className="mt-3 text-sm text-gray-500">{stats.currencies.join(', ') || 'Aucune devise configurée'}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Sécurité</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">Accès global limité</p>
                </div>
                <ShieldCheckIcon className="h-10 w-10 text-indigo-600" />
              </div>
              <p className="mt-3 text-sm text-gray-500">Ce profil gère uniquement les tenants.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <section className="rounded-lg border border-gray-200 bg-white shadow-sm xl:col-span-2">
              <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                <h2 className="text-sm font-semibold text-gray-900">Derniers tenants</h2>
                <Link to="/global/settings" className="text-sm font-medium text-primary-600 hover:text-primary-700">Voir tout</Link>
              </div>
              {recentTenants.length === 0 ? (
                <div className="px-5 py-10 text-center">
                  <BuildingOffice2Icon className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-3 text-sm text-gray-600">Aucun tenant créé.</p>
                  <Link to="/global/settings/new" className="btn-primary mt-4 inline-flex items-center gap-2">
                    <PlusIcon className="h-5 w-5" />
                    Créer un tenant
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Institution</th>
                        <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                        <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Devise</th>
                        <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {recentTenants.map((tenant) => (
                        <tr key={tenant.id} className="hover:bg-gray-50">
                          <td className="px-5 py-4">
                            <div className="font-medium text-gray-900">{tenant.name}</div>
                            <div className="text-sm text-gray-500">Créé le {formatDate(tenant.created_at)}</div>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-700">{tenant.institution_label || typeLabel(tenant.institution_type)}</td>
                          <td className="px-5 py-4 text-sm text-gray-700">{tenant.settings?.general?.currency || 'HTG'}</td>
                          <td className="px-5 py-4 text-right">
                            <Link to={`/global/settings/${tenant.id}`} className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                              <Cog6ToothIcon className="h-4 w-4" />
                              Configurer
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="space-y-6">
              <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-5 py-4">
                  <h2 className="text-sm font-semibold text-gray-900">Modèle Excel d'import</h2>
                </div>
                <div className="space-y-3 p-5">
                  <label className="block text-sm font-medium text-gray-700">
                    Type d'institution
                  </label>
                  <select
                    value={selectedTemplateType}
                    onChange={(event) => setSelectedTemplateType(event.target.value)}
                    className="input-field"
                  >
                    {institutionTemplateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate}
                    className="btn-primary w-full justify-center"
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    {downloadingTemplate ? 'Téléchargement...' : 'Télécharger le modèle'}
                  </button>
                  <p className="text-sm text-gray-500">
                    Le superUser peut télécharger un format adapté avant de créer ou configurer un tenant.
                  </p>
                </div>
              </section>

              <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-5 py-4">
                  <h2 className="text-sm font-semibold text-gray-900">Répartition par type</h2>
                </div>
                <div className="space-y-3 p-5">
                  {Object.keys(stats.byType).length === 0 ? (
                    <p className="text-sm text-gray-500">Aucune répartition disponible.</p>
                  ) : (
                    Object.entries(stats.byType).map(([type, count]) => {
                      const percent = stats.totalTenants ? Math.round((count / stats.totalTenants) * 100) : 0;
                      return (
                        <div key={type}>
                          <div className="mb-1 flex items-center justify-between text-sm">
                            <span className="font-medium text-gray-700">{typeLabel(type)}</span>
                            <span className="text-gray-500">{count}</span>
                          </div>
                          <div className="h-2 rounded-full bg-gray-100">
                            <div className="h-2 rounded-full bg-primary-600" style={{ width: `${percent}%` }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GlobalDashboard;
